/**
 * O RAMAL na tela, como gancho de React.
 *
 * Junta as três peças: a máquina de estado (pura), o telefone (adaptador SIP) e
 * a renovação da credencial. Quem usa vê só situação e ações.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { aplicar, precisaRenovar, situacaoInicial } from "./maquina.js";
export function usarSoftphone(entrada) {
    const [situacao, despachar] = useReducer(aplicar, situacaoInicial);
    const telefone = useRef(null);
    const situacaoRef = useRef(situacao);
    situacaoRef.current = situacao;
    useEffect(() => {
        if (!entrada.ativo)
            return;
        let vivo = true;
        const ligar = async () => {
            const c = await entrada.buscarCredencial();
            if (!vivo)
                return;
            despachar({ tipo: "credencial_recebida", expiraEm: c.expiraEm });
            const t = entrada.criarTelefone
                ? entrada.criarTelefone()
                : new (await import("./jssip.js")).TelefoneJsSip();
            telefone.current = t;
            await t.conectar(c, {
                aoRegistrar: () => despachar({ tipo: "registrou" }),
                aoPerderRegistro: (motivo) => despachar({ tipo: "registro_caiu", ...(motivo ? { motivo } : {}) }),
                aoTocar: () => despachar({ tipo: "tocando" }),
                aoAtender: () => despachar({ tipo: "atendida" }),
                aoEncerrar: (motivo) => despachar({ tipo: "encerrou", ...(motivo ? { motivo } : {}) }),
                aoFalharMicrofone: () => despachar({ tipo: "sem_microfone" }),
            });
        };
        void ligar();
        // A credencial dura minutos. Renovar é rotina, e acontece quase sempre com
        // alguém falando: por isso `renovar` troca a senha sem derrubar a sessão.
        const relogio = setInterval(() => {
            if (!telefone.current || !precisaRenovar(situacaoRef.current))
                return;
            void entrada.buscarCredencial().then((c) => {
                despachar({ tipo: "credencial_recebida", expiraEm: c.expiraEm });
                return telefone.current?.renovar(c);
            }).catch(() => despachar({ tipo: "registro_caiu", motivo: "Não consegui renovar o acesso do ramal." }));
        }, 20_000);
        return () => {
            vivo = false;
            clearInterval(relogio);
            telefone.current?.desconectar();
            telefone.current = null;
            despachar({ tipo: "desligar" });
        };
    }, [entrada.ativo, entrada.buscarCredencial, entrada.criarTelefone]);
    const discar = useCallback(async (destino) => {
        despachar({ tipo: "discou" });
        await telefone.current?.discar(destino);
    }, []);
    const encerrar = useCallback(async () => {
        despachar({ tipo: "encerrando" });
        await telefone.current?.encerrar();
    }, []);
    const mudo = useCallback((ligado) => {
        despachar({ tipo: "mudo", ligado });
        telefone.current?.mudo(ligado);
    }, []);
    const espera = useCallback(async (ligado) => {
        despachar({ tipo: "em_espera", ligado });
        await telefone.current?.espera(ligado);
    }, []);
    return useMemo(() => ({
        situacao, discar, encerrar, mudo, espera,
        atender: async () => { await telefone.current?.atender(); },
        transferir: async (d) => { await telefone.current?.transferir(d); },
        digito: (d) => telefone.current?.digito(d),
    }), [situacao, discar, encerrar, mudo, espera]);
}
//# sourceMappingURL=usarSoftphone.js.map